<template>
    <div id="report-contact-distributions">
        <div class="overall-content">
            <div class="chart-container">
                <div id="time-section" class="chart-section">
                    <h2>{{ $t("report.timeDistribution") }}</h2>
                    <ContactDistributionsByTime
                        :title="$t('report.timeDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byTime"
                    />
                </div>
                <a-divider />
                <div id="channel-section" class="chart-section">
                    <h2>{{ $t("report.channelDistribution") }}</h2>
                    <ContactDistributionsByChannel
                        :title="$t('report.channelDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byChannel"
                    />
                </div>
                <a-divider />
                <div id="tag-section" class="chart-section">
                    <h2>{{ $t("report.tagDistribution") }}</h2>
                    <ContactDistributionsByTag
                        :title="$t('report.tagDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byTag"
                    />
                </div>
                <a-divider />
                <div id="platform-section" class="chart-section">
                    <h2>{{ $t("report.platformDistribution") }}</h2>
                    <ContactDistributionsByPlatform
                        :title="$t('report.platformDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byPlatform"
                    />
                </div>
                <a-divider />
                <div id="owner-section" class="chart-section">
                    <h2>{{ $t("report.ownerDistribution") }}</h2>
                    <ContactDistributionsByOwner
                        :title="$t('report.ownerDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byOwner"
                    />
                </div>
                <a-divider />
                <div id="language-section" class="chart-section">
                    <h2>{{ $t("report.languageDistribution") }}</h2>
                    <ContactDistributionsByLanguage
                        :title="$t('report.languageDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byLanguage"
                    />
                </div>
                <a-divider />
                <div id="ads-section" class="chart-section">
                    <h2>{{ $t("report.adsDistribution") }}</h2>
                    <ContactDistributionsByAd
                        :title="$t('report.adsDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byAd || []"
                    />
                </div>
                <a-divider />
                <div id="ad-name-section" class="chart-section">
                    <h2>{{ $t('report.adNameDistribution') }}</h2>
                    <ContactDistributionsByAd
                        :title="$t('report.adNameDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byAdName || []"
                    />
                </div>
                <a-divider />
                <div id="adset-section" class="chart-section">
                    <h2>{{ $t('report.adsetDistribution') }}</h2>
                    <ContactDistributionsByAd
                        :title="$t('report.adsetDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byAdsetName || []"
                    />
                </div>
                <a-divider />
                <div id="campaign-section" class="chart-section">
                    <h2>{{ $t('report.campaignDistribution') }}</h2>
                    <ContactDistributionsByAd
                        :title="$t('report.campaignDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byCampaignName || []"
                    />
                </div>
                <a-divider />
                <div id="property-section" class="chart-section">
                    <h2>{{ $t("report.contactPropertyDistribution") }}</h2>
                    <ContactDistributionsByContactProperty
                        :title="$t('report.contactPropertyDistribution')"
                        :filters="filters"
                        :loading="loading"
                        :data="data.byContactProperty"
                    />
                </div>
            </div>
        </div>

        <!-- Sağ taraf navigasyon -->
        <div class="section-navigator">
            <div
                v-for="(section, index) in sections"
                :key="section.id"
                class="nav-item"
                :class="{
                    active: activeSection === section.id,
                    [`section-color-${index + 1}`]: true,
                }"
                @click="scrollToSection(section.id)"
                :title="section.title"
            >
                <div class="invisible-hover-area"></div>
                <div class="nav-line"></div>
                <div class="nav-overlay">
                    <span class="nav-text">{{ section.title }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../../utils/log2";
import moment from "moment";
import { REPORT_API } from "../../../api/statistics";
import ContactDistributionsByTime from "./ContactDistributionsByTime.vue";
import ContactDistributionsByChannel from "./ContactDistributionsByChannel.vue";
import ContactDistributionsByTag from "./ContactDistributionsByTag.vue";
import ContactDistributionsByPlatform from "./ContactDistributionsByPlatform.vue";
import ContactDistributionsByOwner from "./ContactDistributionsByOwner.vue";
import ContactDistributionsByLanguage from "./ContactDistributionsByLanguage.vue";
import ContactDistributionsByAd from "./ContactDistributionsByAd.vue";
import ContactDistributionsByContactProperty from "./ContactDistributionsByContactProperty.vue";
import * as echarts from "echarts/lib/echarts.js";

const log = log2("ContactDistributions");
export default {
    name: "ContactDistributions",
    components: {
        ContactDistributionsByTime,
        ContactDistributionsByChannel,
        ContactDistributionsByTag,
        ContactDistributionsByPlatform,
        ContactDistributionsByOwner,
        ContactDistributionsByLanguage,
        ContactDistributionsByAd,
        ContactDistributionsByContactProperty,
    },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            loading: true,
            data: {
                byTime: { buckets: [], timeSeriesType: null },
                byChannel: [],
                byTag: [],
                byPlatform: [],
                byOwner: [],
                byLanguage: [],
                byAd: [],
                byAdName: [],
                byAdsetName: [],
                byCampaignName: [],
                byContactProperty: [],
            },
            activeSection: "time-section",
            sections: [
                { id: "time-section", title: this.$t("report.timeDistribution") },
                { id: "channel-section", title: this.$t("report.channelDistribution") },
                { id: "tag-section", title: this.$t("report.tagDistribution") },
                { id: "platform-section", title: this.$t("report.platformDistribution") },
                { id: "owner-section", title: this.$t("report.ownerDistribution") },
                { id: "language-section", title: this.$t("report.languageDistribution") },
                { id: "ads-section", title: this.$t("report.adsDistribution") },
                { id: "ad-name-section", title: this.$t("report.adNameDistribution") },
                { id: "adset-section", title: this.$t("report.adsetDistribution") },
                { id: "campaign-section", title: this.$t("report.campaignDistribution") },
                { id: "property-section", title: this.$t("report.contactPropertyDistribution") },
            ],
        };
    },
    methods: {
        async fetch() {
            log("fetchData", this.filters);
            try {
                const res = await REPORT_API.contactDistributionsAll(this.filters);
                log("contactDistributionsAll data fetched", res);
                this.data = {
                    ...this.data,
                    ...res,
                    byAd: res.byAd || [],
                    byAdName: res.byAdName || [],
                    byAdsetName: res.byAdsetName || [],
                    byCampaignName: res.byCampaignName || [],
                };
            } catch (error) {
                console.error("Error fetching contact distributions:", error);
            } finally {
                this.loading = false;
            }
        },
        scrollToSection(sectionId) {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        },
        handleScroll() {
            const sections = this.sections.map((s) => document.getElementById(s.id));
            const scrollPosition = window.scrollY + window.innerHeight / 2;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section && section.offsetTop <= scrollPosition) {
                    this.activeSection = this.sections[i].id;
                    break;
                }
            }
        },
        getColorClass(index) {
            return `section-color-${index + 1}`;
        },
    },
    mounted() {
        this.fetch();
        window.addEventListener("scroll", this.handleScroll);
    },
    beforeDestroy() {
        window.removeEventListener("scroll", this.handleScroll);
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
#report-contact-distributions {
    width: 100%;
    height: auto;
    min-height: 100vh;
    display: flex;
    position: relative;

    .md-progress-bar {
        position: absolute;
        top: 0;
        width: 100%;
        margin-left: -10px;
    }

    .side-menubar-wrapper {
        min-height: 100vh;
        flex: 0 0 15%;
        height: 100%;
    }

    .overall-content {
        padding: 10px;
        display: flex;
        flex-direction: column;
        position: relative;
        background: white;
        width: 100%;
        height: 100%;
        min-height: 100vh;
        position: relative;

        .chart-container {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 20px;

            .chart-section {
                display: flex;
                flex-direction: column;
                gap: 10px;

                h2 {
                    text-align: center;
                    font-weight: 700;
                    font-size: 40px;
                }
            }

            .header {
                width: 100%;
            }

            .chart {
                max-width: 1050px;
            }
        }
    }

    .section-navigator {
        position: fixed;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        z-index: 100;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 10px;

        .nav-item {
            position: relative;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            height: 45px;
            transition: all 0.3s ease;

            .invisible-hover-area {
                position: absolute;
                right: 0;
                height: 100%;
                width: 250px;
                z-index: 1;
            }

            .nav-line {
                width: 6px;
                height: 100%;
                border-radius: 3px;
                transition: all 0.3s ease;
                position: relative;
                z-index: 2;
            }

            .nav-overlay {
                position: absolute;
                right: 0;
                height: 100%;
                width: 6px;
                opacity: 0;
                transition: all 0.3s ease;
                border-radius: 3px;
                display: flex;
                align-items: center;
                overflow: hidden;
                z-index: 1;

                .nav-text {
                    color: white;
                    white-space: nowrap;
                    padding: 0 20px;
                    opacity: 0;
                    transition: opacity 0.2s ease 0.1s;
                    font-weight: 500;
                    font-size: 14px;
                }
            }

            &:hover,
            &:hover .nav-overlay {
                .nav-line {
                    width: 8px;
                }

                .nav-overlay {
                    width: 220px;
                    opacity: 1;

                    .nav-text {
                        opacity: 1;
                    }
                }
            }

            &.active {
                .nav-line {
                    height: 55px;
                    width: 8px;
                }
            }

            &.section-color-1 {
                .nav-line {
                    background: rgba(255, 107, 107, 0.8);
                }

                .nav-overlay {
                    background: rgba(255, 107, 107, 0.95);
                }
            }

            &.section-color-2 {
                .nav-line {
                    background: rgba(78, 205, 196, 0.8);
                }

                .nav-overlay {
                    background: rgba(78, 205, 196, 0.95);
                }
            }

            &.section-color-3 {
                .nav-line {
                    background: rgba(69, 183, 209, 0.8);
                }

                .nav-overlay {
                    background: rgba(69, 183, 209, 0.95);
                }
            }

            &.section-color-4 {
                .nav-line {
                    background: rgba(150, 206, 180, 0.8);
                }

                .nav-overlay {
                    background: rgba(150, 206, 180, 0.95);
                }
            }

            &.section-color-5 {
                .nav-line {
                    background: rgba(212, 165, 165, 0.8);
                }

                .nav-overlay {
                    background: rgba(212, 165, 165, 0.95);
                }
            }

            &.section-color-6 {
                .nav-line {
                    background: rgba(155, 93, 229, 0.8);
                }

                .nav-overlay {
                    background: rgba(155, 93, 229, 0.95);
                }
            }

            &.section-color-7 {
                .nav-line {
                    background: rgba(241, 91, 181, 0.8);
                }

                .nav-overlay {
                    background: rgba(241, 91, 181, 0.95);
                }
            }

            &.section-color-8 {
                .nav-line {
                    background: rgba(0, 187, 249, 0.8);
                }

                .nav-overlay {
                    background: rgba(0, 187, 249, 0.95);
                }
            }
        }
    }
}
</style>
